import { AfterViewChecked, Directive, ElementRef, Input, Renderer2 } from '@angular/core';

/*
  PrimeNG 18's ToggleButton still has a style input but no longer applies it anywhere, so the
  [style] on our toggle buttons (the red Show Disabled toggles across the list pages, the large
  options on the event and package pages) was being ignored.

  This picks up the same [style] binding and puts it inline on the toggle itself, as PrimeNG 17 did -
  so it beats the theme exactly as it used to, checked or not. In PrimeNG 18 the toggle is a button
  inside the element; from PrimeNG 19 it is the element. Checked after every view check because
  PrimeNG renders the button itself; a style already in place is left alone.
*/
@Directive({
  //Every toggle - Angular treats [style] as a styling binding, so a [style] attribute selector never matches
  selector: 'p-togglebutton',
  standalone: false
})
export class ToggleButtonStyleDirective implements AfterViewChecked {
  //Plain style="..." arrives as text, so that is read into the same form.
  @Input() set style(value: { [key: string]: string } | string | null) {
    this.current = typeof value == 'string' ? ToggleButtonStyleDirective.parse(value) : value;
  }

  private current: { [key: string]: string } | null = null;

  private static parse(text: string): { [key: string]: string } {
    const parsed: { [key: string]: string } = {};

    text.split(';').forEach(rule => {
      const colon = rule.indexOf(':');
      if (colon > 0)
        parsed[rule.slice(0, colon).trim()] = rule.slice(colon + 1).trim();
    });

    return parsed;
  }

  private applied: { [key: string]: string } | null = null;
  private appliedTo: HTMLElement | null = null;

  constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2) { }

  ngAfterViewChecked(): void {
    const button = this.el.nativeElement.querySelector('button') ?? this.el.nativeElement;
    if (!button || (button === this.appliedTo && this.current === this.applied))
      return;

    //Take off anything the previous style set that the new one does not
    if (this.applied && this.appliedTo === button)
      Object.keys(this.applied).filter(k => !this.current || !(k in this.current)).forEach(k => this.renderer.removeStyle(button, k));

    Object.entries(this.current ?? {}).forEach(([k, v]) => this.renderer.setStyle(button, k, v));

    this.applied = this.current;
    this.appliedTo = button;
  }
}
